import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
/* eslint-disable complexity */

import FocusedTask from '@atlassian/focused-task';
import { ANALYTICS_ACTION_CLICKED, ANALYTICS_ACTION_VIEWED, ButtonClickedEvent, ErrorStateEvent, ScreenViewedEvent, useAnalyticsEvents } from '@atlassian/mpac-analytics';
import { useStatsig } from '@atlassian/mpac-feature-flags';
import { BodySmall, breakpointValues, checkIfEditionChargedSeparatelyInSandboxFromOfferingsByEdition, checkMILEligibilityFromOfferings, checkMILEligibilityFromOfferingsByEdition, checkSandboxEligibilityFromOfferingsByEdition, useEligibleOfferingsForApp, useOrgIdFromCloudId, useResponsiveBreakpoint } from '@atlassian/mpac-primitives';
import { useGetCmtAvailability } from '@atlassian/mpac-site-selection';
import { MarketplaceStoreEditionType } from '@atlassian/mpac-types/graphql-gateway-types';
import { ErrorBoundary } from '@atlassian/mpac-ui';
import { appSupportsAdvancedEdition, isSandboxInstance } from '@atlassian/mpac-utils';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { IntlProvider } from 'react-intl-next';
import { TEST_IDS } from '../constants';
import { ANALYTICS_EVENT_CONTAINER_TWO_STEP_INSTALL_DIALOG, ANALYTICS_SUBJECT_ID_TWO_STEP_INSTALL_DIALOG_BACK_BUTTON, ANALYTICS_SUBJECT_ID_TWO_STEP_INSTALL_DIALOG_CLOSE_BUTTON, ANALYTICS_SUBJECT_ID_TWO_STEP_INSTALL_DIALOG_REVIEW_APP_BUTTON, ANALYTICS_SUBJECT_ID_TWO_STEP_INSTALL_FOCUSED_TASK } from '../events';
import { FocusedTaskErrorBoundaryFallback } from '../FocusedTaskErrorBoundary';
import { useEnableFocusedTaskScroll } from '../hooks/useEnableFocusedTaskScroll';
import { useErrorAndLoadingStates } from '../useErrorAndLoadingStates';
import { getNextButtonText, handleNextButtonClick, isNextButtonDisabled, ProductNameToTargetProductMap, resolveInstallationCTALabel, useGetSiteSelectorData } from '../utils';
import { ReviewAndInstall } from './ReviewAndInstall';
import { TwoClickAppInstallSiteAndPlanSelector } from './SiteSelector';
import { jsx as _jsx } from "react/jsx-runtime";
// eslint-disable-next-line complexity
export const TwoClickAppInstallFocusedTask = /*#__PURE__*/memo(
// eslint-disable-next-line max-lines-per-function
_ref => {
  var _hostStatusPromise$ho, _billingSystemPromise, _selectedSiteAndProdu, _selectedSiteAndProdu2;
  let {
    isFocusedTaskOpen,
    handleFocusedTaskClose,
    links,
    appHeaderDetails,
    appDetails,
    compatibleProducts,
    mpacBaseUrl,
    stargateBasePath,
    graphQLGatewayUrl,
    installAppHrefResolver,
    requestAppHrefResolver,
    installedAppHrefResolver,
    onSiteSelectorFooterInstallCTAClick,
    editionTypes,
    selectedPlan,
    setSelectedPlan,
    showAppPlans
  } = _ref;
  const fireAnalyticsEvent = useAnalyticsEvents();
  const {
    enableMILCustomerSupport,
    enableAppInstallConsentCheck,
    enableFreeAppEditionsView,
    enableLdCustomerGaChanges
  } = useStatsig();

  // Enable scrolling inside the FocusedTask drawer on mobile
  useEnableFocusedTaskScroll(isFocusedTaskOpen);
  const {
    value: breakpoint
  } = useResponsiveBreakpoint();
  const isMobile = breakpoint < breakpointValues.sm;
  const {
    blockInstall,
    updateErrAndLoadingState,
    error
  } = useErrorAndLoadingStates();
  const [errorReported, setErrorReported] = useState(false);
  useEffect(() => {
    if (error && !errorReported) {
      setErrorReported(true);
      fireAnalyticsEvent({
        eventType: ErrorStateEvent,
        event: ANALYTICS_ACTION_VIEWED,
        eventContainer: ANALYTICS_EVENT_CONTAINER_TWO_STEP_INSTALL_DIALOG,
        actionSubjectId: ANALYTICS_SUBJECT_ID_TWO_STEP_INSTALL_FOCUSED_TASK
      });
    }
  }, [error, errorReported, fireAnalyticsEvent]);
  useEffect(() => {
    if (isFocusedTaskOpen) {
      fireAnalyticsEvent({
        eventType: ScreenViewedEvent,
        event: ANALYTICS_ACTION_VIEWED,
        eventContainer: ANALYTICS_EVENT_CONTAINER_TWO_STEP_INSTALL_DIALOG,
        actionSubjectId: ANALYTICS_SUBJECT_ID_TWO_STEP_INSTALL_FOCUSED_TASK
      });
    }
  }, [isFocusedTaskOpen]);
  const {
    isFreeApp,
    isAppInstalled,
    canUserInstallApp,
    selectedSiteAndProduct,
    onSiteChange,
    hostStatusPromise,
    installedAppDetailsPromise,
    availableSitesPromise,
    appInstallPermissionPromise,
    editionInstalledOnSelectedSite,
    installedAppManageLink,
    billingSystemPromise,
    selectedSiteAndProductDetails
  } = useGetSiteSelectorData(appDetails, compatibleProducts, stargateBasePath, showAppPlans, enableLdCustomerGaChanges());
  const {
    hostCmtEnabled,
    billingSystem,
    instanceType
  } = (hostStatusPromise === null || hostStatusPromise === void 0 ? void 0 : (_hostStatusPromise$ho = hostStatusPromise.hostStatusResult) === null || _hostStatusPromise$ho === void 0 ? void 0 : _hostStatusPromise$ho.marketplaceStore.hostStatus) || {};
  const isSandbox = isSandboxInstance(instanceType);
  const isHAMSBilling = (billingSystemPromise === null || billingSystemPromise === void 0 ? void 0 : (_billingSystemPromise = billingSystemPromise.billingSystemResult) === null || _billingSystemPromise === void 0 ? void 0 : _billingSystemPromise.billingSystem) === 'HAMS';
  const isAdvancedEdition = selectedPlan.value === MarketplaceStoreEditionType.Advanced;
  const isAdvancedEditionsWithHAMSBilling = showAppPlans && isAdvancedEdition && isHAMSBilling;
  const {
    cmtAvailabilityResult
  } = useGetCmtAvailability(appDetails.appKey, billingSystem, hostCmtEnabled, selectedSiteAndProduct);
  const enableUserConsentRecording = enableAppInstallConsentCheck();
  const {
    data: eligibleOfferingsForApp
  } = useEligibleOfferingsForApp({
    cloudId: (selectedSiteAndProduct === null || selectedSiteAndProduct === void 0 ? void 0 : (_selectedSiteAndProdu = selectedSiteAndProduct.site) === null || _selectedSiteAndProdu === void 0 ? void 0 : _selectedSiteAndProdu.cloudId) || '',
    appKey: appDetails.appKey || '',
    product: selectedSiteAndProduct !== null && selectedSiteAndProduct !== void 0 && selectedSiteAndProduct.product ? ProductNameToTargetProductMap[selectedSiteAndProduct.product] : undefined,
    canSkip: !showAppPlans && !enableMILCustomerSupport()
  });
  const {
    edition: enterprisePlanPurchased,
    isInstance: isMILEnabledOnSelectedSite
  } = useMemo(() => {
    return checkMILEligibilityFromOfferings(eligibleOfferingsForApp === null || eligibleOfferingsForApp === void 0 ? void 0 : eligibleOfferingsForApp.eligibleOfferings);
  }, [eligibleOfferingsForApp]);
  const appHasMultiInstanceEntitlement = Boolean(enableMILCustomerSupport() && isMILEnabledOnSelectedSite);
  const isMILandAppEditionsEnabled = Boolean(showAppPlans && enableMILCustomerSupport() && !isSandbox && selectedPlan.value && checkMILEligibilityFromOfferingsByEdition(eligibleOfferingsForApp === null || eligibleOfferingsForApp === void 0 ? void 0 : eligibleOfferingsForApp.eligibleOfferings, selectedPlan.value));
  const resolveInstallationCTA = () => {
    var _installedAppDetailsP, _installedAppDetailsP2;
    return resolveInstallationCTALabel({
      isFreeApp,
      isCloudMigrationTrialEligible: !!(cmtAvailabilityResult !== null && cmtAvailabilityResult !== void 0 && cmtAvailabilityResult.marketplaceStore.cmtAvailability.allowed),
      isEditionsSupported: showAppPlans,
      isEditionChargedSeparatelyInSandbox: checkIfEditionChargedSeparatelyInSandboxFromOfferingsByEdition(eligibleOfferingsForApp === null || eligibleOfferingsForApp === void 0 ? void 0 : eligibleOfferingsForApp.eligibleOfferings, selectedPlan === null || selectedPlan === void 0 ? void 0 : selectedPlan.value),
      eligibleForFreeOnSandboxWithEditions: showAppPlans && checkSandboxEligibilityFromOfferingsByEdition(eligibleOfferingsForApp === null || eligibleOfferingsForApp === void 0 ? void 0 : eligibleOfferingsForApp.eligibleOfferings, selectedPlan === null || selectedPlan === void 0 ? void 0 : selectedPlan.value),
      isEligibleForFreeOnSandbox: !!(installedAppDetailsPromise !== null && installedAppDetailsPromise !== void 0 && (_installedAppDetailsP = installedAppDetailsPromise.installedAppDetailsResult) !== null && _installedAppDetailsP !== void 0 && (_installedAppDetailsP2 = _installedAppDetailsP.marketplaceStore.installedAppDetails) !== null && _installedAppDetailsP2 !== void 0 && _installedAppDetailsP2.paidLicenseActiveOnParent),
      isEligibleForMIL: appHasMultiInstanceEntitlement && !showAppPlans && !isSandbox,
      isMILandAppEditionsEnabled,
      selectedPlan: selectedPlan === null || selectedPlan === void 0 ? void 0 : selectedPlan.value
    });
  };
  const defaultSelectedUserSelection = enableLdCustomerGaChanges() ? 'selectUsers' : 'selectAllUsers';
  const defaultUserCount = enableLdCustomerGaChanges() ? 10 : 0;
  const [currentStep, setCurrentStep] = useState(0);
  const [isInstallButtonLoading, setIsInstallButtonLoading] = useState(false);
  const [selectedUserSelection, setSelectedUserSelection] = useState(defaultSelectedUserSelection);
  const [userCount, setUserCount] = useState(defaultUserCount);
  const isAdvancedEditionAvailable = appSupportsAdvancedEdition(editionTypes);
  const nextButtonText = getNextButtonText(isAppInstalled !== null && isAppInstalled !== void 0 ? isAppInstalled : false, canUserInstallApp, installedAppManageLink, isAdvancedEditionAvailable, editionInstalledOnSelectedSite, showAppPlans);
  const {
    data: orgId
  } = useOrgIdFromCloudId({
    cloudId: (selectedSiteAndProduct === null || selectedSiteAndProduct === void 0 ? void 0 : selectedSiteAndProduct.site.cloudId) || '',
    skipQuery: !showAppPlans
  });
  const handleOnClose = useCallback(() => {
    setCurrentStep(0);
    setSelectedUserSelection('selectUsers');
    setUserCount(10);
    onSiteChange({});
    fireAnalyticsEvent({
      eventType: ButtonClickedEvent,
      event: ANALYTICS_ACTION_CLICKED,
      eventContainer: ANALYTICS_EVENT_CONTAINER_TWO_STEP_INSTALL_DIALOG,
      actionSubjectId: ANALYTICS_SUBJECT_ID_TWO_STEP_INSTALL_DIALOG_CLOSE_BUTTON
    });
    handleFocusedTaskClose();
  }, [fireAnalyticsEvent, handleFocusedTaskClose, onSiteChange]);
  const handleCancelButtonClick = useCallback(() => {
    if (currentStep <= 0) {
      onSiteChange({});
      fireAnalyticsEvent({
        eventType: ButtonClickedEvent,
        event: ANALYTICS_ACTION_CLICKED,
        eventContainer: ANALYTICS_EVENT_CONTAINER_TWO_STEP_INSTALL_DIALOG,
        actionSubjectId: ANALYTICS_SUBJECT_ID_TWO_STEP_INSTALL_DIALOG_CLOSE_BUTTON
      });
      handleFocusedTaskClose();
    } else {
      fireAnalyticsEvent({
        eventType: ButtonClickedEvent,
        event: ANALYTICS_ACTION_CLICKED,
        eventContainer: ANALYTICS_EVENT_CONTAINER_TWO_STEP_INSTALL_DIALOG,
        actionSubjectId: ANALYTICS_SUBJECT_ID_TWO_STEP_INSTALL_DIALOG_BACK_BUTTON
      });
      setCurrentStep(prev => prev - 1);
    }
  }, [currentStep, fireAnalyticsEvent, handleFocusedTaskClose, onSiteChange]);
  const handleReviewButtonClick = useCallback(async () => {
    setIsInstallButtonLoading(true);
    try {
      const shouldIncrementStep = await handleNextButtonClick({
        selectedSiteAndProduct,
        isAppInstalled: isAppInstalled !== null && isAppInstalled !== void 0 ? isAppInstalled : false,
        currentStep,
        nextButtonText: nextButtonText.toString(),
        resolveInstallationCTA: resolveInstallationCTA(),
        onSiteSelectorFooterInstallCTAClick,
        requestAppHrefResolver,
        installAppHrefResolver,
        installedAppHrefResolver,
        installedAppManageLink,
        steps,
        links,
        selectedPlan: showAppPlans ? selectedPlan : undefined,
        orgId,
        appDetails,
        fireAnalyticsEvent,
        enableUserConsentRecording,
        selectedUserSelection,
        userCount: selectedUserSelection === 'selectUsers' ? userCount : undefined
      });
      if (shouldIncrementStep) {
        fireAnalyticsEvent({
          eventType: ButtonClickedEvent,
          event: ANALYTICS_ACTION_CLICKED,
          eventContainer: ANALYTICS_EVENT_CONTAINER_TWO_STEP_INSTALL_DIALOG,
          actionSubjectId: ANALYTICS_SUBJECT_ID_TWO_STEP_INSTALL_DIALOG_REVIEW_APP_BUTTON
        });
        setCurrentStep(prev => prev + 1);
      }
    } finally {
      setIsInstallButtonLoading(false);
    }
  }, [selectedSiteAndProduct, isAppInstalled, currentStep, nextButtonText, resolveInstallationCTA, onSiteSelectorFooterInstallCTAClick, requestAppHrefResolver, installAppHrefResolver, installedAppHrefResolver, installedAppManageLink, selectedPlan, orgId, enableUserConsentRecording]);
  const isPrimaryCtaLoading = selectedSiteAndProduct && appInstallPermissionPromise.appInstallPermissionPending || installedAppDetailsPromise.installedAppDetailsLoading || (billingSystemPromise === null || billingSystemPromise === void 0 ? void 0 : billingSystemPromise.billingSystemPending);
  const memoizedOnSiteChange = useCallback(onSiteChange, [onSiteChange]);
  const memoizedAvailableSitesPromise = useMemo(() => availableSitesPromise, [availableSitesPromise]);
  const memoizedAppInstallPermissionPromise = useMemo(() => appInstallPermissionPromise, [appInstallPermissionPromise]);
  const steps = [_objectSpread({
    id: 'select-a-site-step',
    label: showAppPlans ? enableFreeAppEditionsView() ? 'Select a site and plan' : 'Select an edition and site' : 'Select a site',
    type: isMobile ? 'information' : 'progress',
    content: /*#__PURE__*/_jsx(TwoClickAppInstallSiteAndPlanSelector, {
      appDetails: appDetails,
      isFreeApp: isFreeApp,
      canUserInstallApp: canUserInstallApp,
      appCompatibilities: compatibleProducts,
      mpacBaseUrl: mpacBaseUrl,
      availableSitesPromise: memoizedAvailableSitesPromise,
      appInstallPermissionPromise: memoizedAppInstallPermissionPromise,
      onSiteChange: memoizedOnSiteChange,
      isAppInstalled: isAppInstalled !== null && isAppInstalled !== void 0 ? isAppInstalled : false,
      hostStatusPromise: hostStatusPromise,
      installedAppDetailsResult: installedAppDetailsPromise === null || installedAppDetailsPromise === void 0 ? void 0 : installedAppDetailsPromise.installedAppDetailsResult,
      selectedSiteAndProduct: selectedSiteAndProduct,
      selectedSiteAndProductDetails: selectedSiteAndProductDetails,
      links: links,
      editionInstalledOnSelectedSite: editionInstalledOnSelectedSite,
      editionTypes: editionTypes,
      selectedPlan: selectedPlan,
      setSelectedPlan: setSelectedPlan,
      showAppPlans: showAppPlans,
      orgId: orgId,
      enterprisePlanPurchased: enterprisePlanPurchased,
      appHasMultiInstanceEntitlement: appHasMultiInstanceEntitlement,
      eligibleOfferings: eligibleOfferingsForApp === null || eligibleOfferingsForApp === void 0 ? void 0 : eligibleOfferingsForApp.eligibleOfferings,
      isAdvancedEditionsWithHAMSBilling: isAdvancedEditionsWithHAMSBilling,
      selectedUserSelection: selectedUserSelection,
      onUserSelectionChange: setSelectedUserSelection,
      userCount: userCount,
      onUserCountChange: setUserCount
    }),
    backButtonText: 'Cancel',
    backButtonOnClick: handleCancelButtonClick,
    isSubmissionLoading: isPrimaryCtaLoading,
    nextButtonText,
    isNextButtonDisabled: isNextButtonDisabled(!!isAppInstalled, installedAppManageLink, canUserInstallApp, isAdvancedEditionsWithHAMSBilling, isAdvancedEditionAvailable, selectedSiteAndProduct, editionInstalledOnSelectedSite),
    nextButtonOnClick: handleReviewButtonClick,
    contentSize: 'large'
  }, canUserInstallApp && isAppInstalled && {
    footerInfoText: /*#__PURE__*/_jsx(BodySmall, {
      style: {
        fontWeight: "var(--ds-font-weight-regular, 400)"
      },
      children: "This app has already been added to your instance"
    })
  }), {
    id: 'review-and-install-step',
    label: 'Review and install',
    type: isMobile ? 'information' : 'progress',
    content: /*#__PURE__*/_jsx(ReviewAndInstall, {
      appHeaderDetails: appHeaderDetails,
      links: links,
      appDetails: appDetails,
      hostStatusResponse: hostStatusPromise || {},
      installedAppDetailsResult: installedAppDetailsPromise === null || installedAppDetailsPromise === void 0 ? void 0 : installedAppDetailsPromise.installedAppDetailsResult,
      cmtAvailabilityResult: cmtAvailabilityResult,
      graphQLGatewayUrl: graphQLGatewayUrl,
      cloudId: selectedSiteAndProduct === null || selectedSiteAndProduct === void 0 ? void 0 : selectedSiteAndProduct.site.cloudId,
      updateErrAndLoadingState: updateErrAndLoadingState,
      selectedPlan: selectedPlan,
      showAppPlans: showAppPlans,
      enterprisePlanPurchased: enterprisePlanPurchased,
      appHasMultiInstanceEntitlement: appHasMultiInstanceEntitlement,
      eligibleOfferings: eligibleOfferingsForApp === null || eligibleOfferingsForApp === void 0 ? void 0 : eligibleOfferingsForApp.eligibleOfferings,
      editionTypes: editionTypes,
      selectedUserSelection: selectedUserSelection,
      userCount: userCount,
      selectedSiteUrl: selectedSiteAndProduct === null || selectedSiteAndProduct === void 0 ? void 0 : (_selectedSiteAndProdu2 = selectedSiteAndProduct.site) === null || _selectedSiteAndProdu2 === void 0 ? void 0 : _selectedSiteAndProdu2.url
    }),
    backButtonText: 'Back',
    backButtonOnClick: handleCancelButtonClick,
    nextButtonText: resolveInstallationCTA(),
    nextButtonOnClick: handleReviewButtonClick,
    contentSize: 'xlarge',
    isNextButtonDisabled: blockInstall,
    isSubmissionLoading: isInstallButtonLoading
  }];
  return /*#__PURE__*/_jsx(ErrorBoundary, {
    boundaryName: "TwoClickAppInstallMpac",
    fallback: /*#__PURE__*/_jsx(FocusedTaskErrorBoundaryFallback, {
      isOpen: isFocusedTaskOpen,
      onClose: handleOnClose
    }),
    children: /*#__PURE__*/_jsx(IntlProvider, {
      locale: "en",
      children: /*#__PURE__*/_jsx(FocusedTask, {
        testId: TEST_IDS.TWO_CLICK_APP_INSTALL_MPAC_FOCUSED_TASK,
        currentStep: currentStep,
        isOpen: isFocusedTaskOpen,
        steps: steps,
        onClose: handleOnClose
      })
    })
  });
});
TwoClickAppInstallFocusedTask.displayName = 'TwoClickAppInstallFocusedTask';